"use client";

import axios from "axios";
import { useState, useCallback, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  ComponentCmsDataFormGroup,
  ComponentCmsDataFormQuestion,
  ComponentCmsDataFormQchoice,
  Enum_Componentcmsdataformqchoice_Jump_Condition_Operation,
  Enum_Componentcmsdataformquestion_Type,
  Enum_Componentcmsdataformquestion_Desktop_Layout,
  Enum_Componentcmsdataformquestion_Mobile_Layout,
} from "@/generated";
import clsx from "clsx";
import { useValidateChoice } from "./hooks/useValidateChoice";
import { QuestionContent } from "./components/QuestionContent";
import { ChoicesGrid } from "./components/ChoicesGrid";
import { BackgroundMedia } from "./components/BackgroundMedia";
import { useViewport } from "@/hooks/useViewport";
import { useRouter } from "next/navigation";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

interface LFormGroupProps {
  data: ComponentCmsDataFormGroup;
}

interface FeedbackData {
  text?: string | null;
  media?: ComponentCmsDataFormQuestion["feedback_media"] | null;
}

export const LFormGroup: React.FC<LFormGroupProps> = ({ data }) => {
  const {
    questions = [],
    title,
    type,
    ask_for_rfid,
    house_videos,
    redirectUrl,
    random_questions,
    number_of_random_questions,
    show_start_screen,
    thank_you_message,
    thank_you_message_color,
    thank_you_message_background,
  } = data;
  const [questionHistory, setQuestionHistory] = useState<number[]>([0]);
  const [historyPosition, setHistoryPosition] = useState(0);
  const [answers, setAnswers] = useState<
    Record<number, ComponentCmsDataFormQchoice | ComponentCmsDataFormQchoice[]>
  >({});
  const [feedback, setFeedback] = useState<FeedbackData | null>(null);
  const [otherAnswer, setOtherAnswer] = useState("");
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [direction, setDirection] = useState<"forward" | "back">("forward");
  const [reservationId, setReservationId] = useState<string | null>(null);
  const currentQuestionIdx = questionHistory[historyPosition];
  const currentQuestion = (questions || [])[
    currentQuestionIdx
  ] as ComponentCmsDataFormQuestion;

  const validateChoice = useValidateChoice(currentQuestion, otherAnswer);
  const { isMobile, isLoading } = useViewport();
  const router = useRouter();
  const { t: tCommon } = useTranslation("common");
  const [showThankYouMessage, setShowThankYouMessage] =
    useState<boolean>(false);
  const [nextQuestionToPreload, setNextQuestionToPreload] = useState<
    number | null
  >(null);

  const fullScreen =
    questions?.[0]?.desktop_layout !==
    Enum_Componentcmsdataformquestion_Desktop_Layout.StartScreen;

  const handleSubmit = useCallback(
    async (
      answers: Record<number, ComponentCmsDataFormQchoice>,
      showToast = true
    ) => {
      const questionIndexes = Object.keys(answers);
      const dataToSumit = questionIndexes.map((questionIndex) => {
        const answer = answers[parseInt(questionIndex)];
        if (Array.isArray(answer)) {
          return {
            questionIndex: parseInt(questionIndex),
            questionId: questions?.[parseInt(questionIndex) - 1]?.id,
            questionTitle: questions?.[parseInt(questionIndex) - 1]?.title,
            questionType: questions?.[parseInt(questionIndex) - 1]?.type,
            response: answer.map((a) => {
              return {
                id: a.id,
                text: a.choice_text,
                shortcut: a.choice_shortcut,
                media: a.choice_media?.data?.attributes?.url,
              };
            }),
          };
        }
        return {
          questionIndex: parseInt(questionIndex),
          questionId: questions?.[parseInt(questionIndex) - 1]?.id,
          questionTitle: questions?.[parseInt(questionIndex) - 1]?.title,
          questionType: questions?.[parseInt(questionIndex) - 1]?.type,
          response: {
            id: answer.id,
            text: answer.choice_text,
            shortcut: answer.choice_shortcut,
            media: answer.choice_media?.data?.attributes?.url,
          },
        };
      });

      // Submit the data to ezform.
      try {
        await axios.post(
          `${process.env.NEXT_PUBLIC_API_URL}/api/ezforms/submit`,
          {
            formName: "form-submission",
            formData: {
              questions: dataToSumit,
              formGroupId: data.id,
            },
            // Include reservation_id if available
            ...(reservationId && { reservationId }),
          }
        );

        if (showToast) {
          toast.success(tCommon("succesfully-sent"));
          setAnswers({});
          setOtherAnswer("");
        }
        setShowThankYouMessage(true);
        redirectUrl && router?.push(redirectUrl);
      } catch (error) {
        console.error(error);
        toast.error(tCommon("error-occurred"));
      }
    },
    [questions, redirectUrl, router, tCommon, reservationId, data.id]
  );

  // Get reservation_id from URL parameters
  useEffect(() => {
    if (typeof window !== "undefined") {
      const urlParams = new URLSearchParams(window.location.search);
      const reservationIdParam = urlParams.get("reservation_id");
      if (reservationIdParam) {
        setReservationId(reservationIdParam);
      }
    }
  }, []);

  const getNextSequentialIndex = useCallback(
    (currentIndex: number) => {
      const sortedIndices = questions
        ?.map((q) => q?.index)
        .filter((index): index is number => index !== undefined)
        .sort((a, b) => a - b);

      const nextIndex = sortedIndices?.find((index) => index > currentIndex);
      return nextIndex ?? -1;
    },
    [questions]
  );

  const handleChoiceSelection = useCallback(
    (choice: ComponentCmsDataFormQchoice | ComponentCmsDataFormQchoice[]) => {
      if (!currentQuestion?.index) return;

      // For multiple choices
      if (Array.isArray(choice)) {
        // Update answers with current choice
        const updatedAnswers = {
          ...answers,
          [currentQuestion.index]: choice,
        };

        const nextIdx = currentQuestion?.next_question_index
          ? currentQuestion.next_question_index
          : getNextSequentialIndex(currentQuestion.index);

        // Find question with matching index
        const nextQuestionIndex = questions?.findIndex(
          (q) => q?.index === nextIdx
        );
        if (nextQuestionIndex === -1 || nextQuestionIndex === undefined) {
          // Submit with updated answers instead of mixing with existing answers
          handleSubmit(
            updatedAnswers as Record<number, ComponentCmsDataFormQchoice>,
            true
          );
          setDirection("forward");
          return;
        }

        setAnswers(updatedAnswers);
        setQuestionHistory((prev) => [
          ...prev.slice(0, historyPosition + 1),
          nextQuestionIndex,
        ]);
        setHistoryPosition((prev) => prev + 1);
        setDirection("forward");
        return;
      }

      // For single choice
      if (!validateChoice(choice)) return;

      setOtherAnswer("");
      const updatedAnswers = {
        ...answers,
        [currentQuestion.index]: choice,
      };

      // Calculate next question index before showing feedback
      const getNextQuestionIndex = (choice: ComponentCmsDataFormQchoice) => {
        if (
          choice.jump_to_question_index &&
          choice.jump_to_question_index > 0
        ) {
          return choice.jump_to_question_index;
        }

        // Default to next sequential question
        return getNextSequentialIndex(currentQuestion.index);
      };

      const nextIdx = getNextQuestionIndex(choice);
      const nextQuestionIndex = questions?.findIndex(
        (q) => q?.index === nextIdx
      );

      // Set the next question to preload
      setNextQuestionToPreload(nextQuestionIndex ?? null);

      // Handle feedback
      const showFeedback = () => {
        // Priority: choice feedback media > choice feedback text > question feedback media > question feedback text
        const feedbackData: FeedbackData = {
          media:
            choice.feedback_media || currentQuestion.feedback_media || null,
          text:
            choice.feedback_media_text ||
            currentQuestion.feedback_media_text ||
            null,
        };

        if (feedbackData.media?.data?.attributes?.url || feedbackData.text) {
          setFeedback(feedbackData);

          // For media feedback, let the media component handle the duration
          if (feedbackData.media?.data?.attributes?.url) {
            return new Promise((resolve) => {
              const element = new Audio(
                feedbackData.media?.data?.attributes?.url
              );
              element.onended = resolve;
              element.play();
            });
          }

          // For text feedback, use a 3-second timeout
          return new Promise((resolve) => setTimeout(resolve, 3000));
        }

        return Promise.resolve();
      };

      // Show feedback then proceed with navigation
      showFeedback().then(() => {
        setFeedback(null);

        if (nextQuestionIndex === -1 || nextQuestionIndex === undefined) {
          // Form is complete - show thank you message and redirect if needed
          handleSubmit(
            updatedAnswers as Record<number, ComponentCmsDataFormQchoice>,
            true
          );
          setShowThankYouMessage(true);

          // Handle redirect if URL is provided
          if (redirectUrl) {
            setTimeout(() => {
              router.push(redirectUrl);
            }, 2000); // Short delay before redirect
          }
          return;
        }

        setAnswers(updatedAnswers);
        setQuestionHistory((prev) => [
          ...prev.slice(0, historyPosition + 1),
          nextQuestionIndex,
        ]);
        setHistoryPosition((prev) => prev + 1);
        setDirection("forward");
        setNextQuestionToPreload(null); // Reset preloaded question
      });
    },
    [
      currentQuestion,
      historyPosition,
      answers,
      validateChoice,
      questions,
      getNextSequentialIndex,
      redirectUrl,
      handleSubmit,
      router,
    ]
  );

  const handleOtherSubmit = (otherAnswer: string) => {
    setOtherAnswer(otherAnswer);

    // Create base choice object
    const otherChoice: ComponentCmsDataFormQchoice = {
      choice_text: otherAnswer,
      display_delay: 0,
      id: `other_${currentQuestion.index}`,
      jump_condition_operation:
        Enum_Componentcmsdataformqchoice_Jump_Condition_Operation.ResponseIsEqualWithChoice,
      jump_to_question_index: 0,
    };

    let nextQuestionIndex = currentQuestion.next_question_index;

    // For single choice questions, try to match with existing choices
    if (
      currentQuestion.type ===
      Enum_Componentcmsdataformquestion_Type.SingleChoice
    ) {
      const matchingChoice = currentQuestion.choices?.find((choice) => {
        if (!choice?.jump_condition_operation || !choice.choice_text)
          return false;

        switch (choice.jump_condition_operation) {
          case "response_is_equal_with_choice":
            return otherAnswer === choice.choice_text;
          case "response_is_not_equal_with_choice":
            return otherAnswer !== choice.choice_text;
          default:
            return false;
        }
      });

      if (matchingChoice?.jump_to_question_index) {
        nextQuestionIndex = matchingChoice.jump_to_question_index;
      }
    }
    // For numeric/date/time types, evaluate conditions
    else if (
      [
        Enum_Componentcmsdataformquestion_Type.Number,
        Enum_Componentcmsdataformquestion_Type.Date,
        Enum_Componentcmsdataformquestion_Type.Time,
        Enum_Componentcmsdataformquestion_Type.DateAndTime,
        Enum_Componentcmsdataformquestion_Type.Birthday,
      ].includes(currentQuestion.type as Enum_Componentcmsdataformquestion_Type)
    ) {
      const numericResponse =
        currentQuestion.type === Enum_Componentcmsdataformquestion_Type.Number
          ? parseFloat(otherAnswer)
          : new Date(otherAnswer).getTime();

      // Find the first choice that matches our condition
      const matchingChoice = currentQuestion.choices?.find((choice) => {
        if (!choice) return false;

        const choiceValue =
          currentQuestion.type === Enum_Componentcmsdataformquestion_Type.Number
            ? parseFloat(choice.choice_text || "0")
            : new Date(choice.choice_text || "").getTime();

        switch (choice.jump_condition_operation) {
          case "response_is_greater_than_choice":
            return numericResponse > choiceValue;
          case "response_is_less_than_choice":
            return numericResponse < choiceValue;
          case "response_is_equal_with_choice":
            return numericResponse === choiceValue;
          default:
            return false;
        }
      });

      if (matchingChoice) {
        nextQuestionIndex = matchingChoice.jump_to_question_index;
      }
    } else if (
      [
        Enum_Componentcmsdataformquestion_Type.ShortText,
        Enum_Componentcmsdataformquestion_Type.LongText,
        Enum_Componentcmsdataformquestion_Type.Email,
        Enum_Componentcmsdataformquestion_Type.FirstName,
        Enum_Componentcmsdataformquestion_Type.LastName,
        Enum_Componentcmsdataformquestion_Type.PhoneNumber,
      ].includes(currentQuestion.type as Enum_Componentcmsdataformquestion_Type)
    ) {
      // Find the first choice that matches our text condition
      const matchingChoice = currentQuestion.choices?.find((choice) => {
        if (!choice) return false;

        switch (choice.jump_condition_operation) {
          case "response_is_equal_with_choice":
            return otherAnswer === choice.choice_text;
          case "response_is_not_equal_with_choice":
            return otherAnswer !== choice.choice_text;
          default:
            return false;
        }
      });

      if (matchingChoice) {
        nextQuestionIndex = matchingChoice.jump_to_question_index;
      }
    }

    // If no specific navigation found, use next question index or default to next in sequence
    if (!nextQuestionIndex) {
      nextQuestionIndex = currentQuestionIdx + 1;
    }

    // Instead of updating answers and calling handleChoiceSelection separately,
    // just call handleChoiceSelection with the prepared otherChoice
    otherChoice.jump_to_question_index = nextQuestionIndex;
    handleChoiceSelection(otherChoice);
  };

  // New function to update history when going back
  const goBackToHistory = (targetIndex: number) => {
    setQuestionHistory((prevHistory) => {
      // Ensure the target index is valid
      if (targetIndex < 0 || targetIndex >= prevHistory.length) {
        return prevHistory;
      }
      // Trim the history so that only entries up to the target index are kept
      return prevHistory.slice(0, targetIndex + 1);
    });
    setHistoryPosition(targetIndex);
  };

  // Updated handlePrevious that clears forward history when going back
  const handlePrevious = () => {
    if (historyPosition > 0) {
      // In this case we move one step back.
      goBackToHistory(historyPosition - 1);
      setDirection("back");
    }
  };

  const handleNext = () => {
    if (historyPosition < questionHistory.length - 1) {
      setHistoryPosition((prev) => prev + 1);
      setDirection("forward");
    }
  };

  const canGoBack = historyPosition > 0;
  const canGoForward = historyPosition < questionHistory.length - 1;

  useEffect(() => {
    // Set isInitialLoad to false after the first render
    setIsInitialLoad(false);
  }, []);

  // Function to reset the form to initial state
  const resetForm = useCallback(() => {
    // Submit the form as it is, then reset form.
    if (Object.keys(answers).length > 0) {
      handleSubmit(
        answers as Record<number, ComponentCmsDataFormQchoice>,
        false
      );
    }
    // Reset to first question
    setQuestionHistory([0]);
    setHistoryPosition(0);

    // Clear all answers
    setAnswers({});

    // Reset other form state
    setOtherAnswer("");
    setFeedback(null);
    setDirection("forward");

    // Set initial load animation
    setIsInitialLoad(true);

    // Reset any preloaded questions
    setNextQuestionToPreload(null);

    // After a brief delay, set isInitialLoad back to false for proper animations
    setTimeout(() => {
      setIsInitialLoad(false);
    }, 100);
  }, [answers, handleSubmit]);

  // Add an effect to handle the beforeunload event
  useEffect(() => {
    // Only add the event listener if we have answers to submit
    if (Object.keys(answers).length > 0) {
      const handleBeforeUnload = (event: BeforeUnloadEvent) => {
        // Submit the form data when the user is about to leave
        handleSubmit(
          answers as Record<number, ComponentCmsDataFormQchoice>,
          false
        );

        // The following is standard code to show a confirmation dialog
        // Note: Modern browsers often ignore custom messages for security reasons
        event.preventDefault();
        event.returnValue = "";
        return "";
      };

      window.addEventListener("beforeunload", handleBeforeUnload);

      // Clean up the event listener when the component unmounts
      return () => {
        window.removeEventListener("beforeunload", handleBeforeUnload);
      };
    }
  }, [answers, handleSubmit]);

  // Optional: Show a loading state while determining viewport
  if (isLoading) {
    return (
      <div
        className={clsx(
          "w-full flex items-center justify-center bg-transparent text-white",
          fullScreen && "min-h-screen"
        )}
      >
        &nbsp;
      </div>
    );
  }

  // Add thank you message display
  if (showThankYouMessage) {
    return (
      <div
        className={clsx(
          "relative w-full flex flex-col overflow-hidden justify-center items-center p-8",
          fullScreen && "min-h-screen"
        )}
        style={{
          backgroundColor: thank_you_message_background || "#1a365d",
          color: thank_you_message_color || "white",
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center max-w-2xl"
        >
          <p className="!text-xl">
            {thank_you_message ||
              tCommon("thank-you-for-submitting-message") ||
              "Your submission has been successfully sent."}
          </p>
        </motion.div>
      </div>
    );
  }

  // Get the next question to preload
  const preloadQuestion =
    nextQuestionToPreload !== null && questions
      ? (questions[nextQuestionToPreload] as ComponentCmsDataFormQuestion)
      : null;

  return (
    <div
      className={clsx([
        "w-full flex flex-col",
        "bg-ekblue text-white",
        currentQuestion?.desktop_layout !==
          Enum_Componentcmsdataformquestion_Desktop_Layout.StartScreen &&
          "min-h-screen",
        currentQuestion?.submit_button_sticky && isMobile
          ? "fixed bottom-0 left-0 right-0 z-50 h-screen overflow-scroll py-14"
          : "relative",
      ])}
      style={{
        backgroundColor: currentQuestion?.background_color || "#1a365d",
      }}
    >
      <AnimatePresence mode="wait">
        {feedback ? (
          <motion.div
            key="feedback"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 w-full h-full flex items-center justify-center bg-transparent z-50"
          >
            {feedback.media?.data?.attributes?.url ? (
              feedback.media.data?.attributes?.mime?.startsWith("video/") ? (
                <video
                  className="w-full h-full object-cover"
                  src={feedback.media.data?.attributes?.url}
                  autoPlay
                  playsInline
                  muted={false}
                />
              ) : feedback.media.data?.attributes?.mime?.startsWith(
                  "audio/"
                ) ? (
                <audio
                  className="w-full max-w-md"
                  src={feedback.media.data?.attributes?.url}
                  autoPlay
                  playsInline
                  controls
                />
              ) : (
                <img
                  className="w-full h-full object-cover"
                  src={feedback.media.data?.attributes?.url}
                  alt="Feedback"
                />
              )
            ) : (
              <div className="text-center p-8 max-w-2xl">
                <p className="text-2xl font-medium text-white">
                  {feedback.text}
                </p>
              </div>
            )}

            {/* Preload next question in hidden div */}
            {preloadQuestion && (
              <div className="hidden">
                <PreloadNextQuestion
                  question={preloadQuestion}
                  isMobile={isMobile}
                />
              </div>
            )}
          </motion.div>
        ) : (
          <motion.div
            key={currentQuestion?.index}
            initial={{
              opacity: 0,
              x: isInitialLoad
                ? 0
                : direction === "back"
                  ? "-100%" // Coming from left when going back
                  : "100%", // Coming from right when going forward
            }}
            animate={{
              opacity: 1,
              x: 0,
              y: isInitialLoad ? ["-20%", "0%"] : 0,
            }}
            transition={{
              type: "tween",
              duration: 0.4,
              ease: "easeInOut",
            }}
            className="w-full"
          >
            {isMobile ? (
              <>
                {/* Mobile layout */}
                <div className="relative lg:hidden block">
                  {/* For full_background mobile layout, image is in the background */}
                  {currentQuestion.mobile_layout ===
                    Enum_Componentcmsdataformquestion_Mobile_Layout.FullBackground && (
                    <>
                      {/* Background image / video */}
                      <BackgroundMedia
                        question={currentQuestion}
                        displayType="mobile"
                        layout={currentQuestion.mobile_layout}
                      />

                      {/* Content with higher z-index */}
                      <div
                        className={clsx([
                          "relative z-10 min-h-screen flex flex-col max-w-sm mx-auto",
                          {
                            "justify-start":
                              currentQuestion.vertical_alignment === "top",
                            "justify-center":
                              currentQuestion.vertical_alignment === "middle",
                            "justify-end":
                              currentQuestion.vertical_alignment === "bottom",
                          },
                        ])}
                      >
                        <div className="p-4">
                          <QuestionContent question={currentQuestion} />
                          {/* Choices grid */}
                          <ChoicesGrid
                            question={currentQuestion}
                            onChoiceSelect={handleChoiceSelection}
                            handleOtherSubmit={handleOtherSubmit}
                            onPrevious={handlePrevious}
                            onNext={handleNext}
                            canGoBack={canGoBack}
                            canGoForward={canGoForward}
                            isMobile={true}
                            resetForm={resetForm}
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {/* For landscape_image_middle or landscape_image_top layout, the image is on top of question or bellow */}
                  {(currentQuestion.mobile_layout ===
                    Enum_Componentcmsdataformquestion_Mobile_Layout.LandscapeImageMiddle ||
                    currentQuestion.mobile_layout ===
                      Enum_Componentcmsdataformquestion_Mobile_Layout.LandscapeImageTop ||
                    currentQuestion.mobile_layout ===
                      Enum_Componentcmsdataformquestion_Mobile_Layout.StartScreen) && (
                    <div
                      className={clsx([
                        "flex flex-col gap-8 p-4 max-w-sm mx-auto",
                        currentQuestion.mobile_layout !==
                          Enum_Componentcmsdataformquestion_Mobile_Layout.StartScreen &&
                          "min-h-screen",
                        // Add vertical alignment classes
                        {
                          "justify-start":
                            currentQuestion.vertical_alignment === "top",
                          "justify-center":
                            currentQuestion.vertical_alignment === "middle",
                          "justify-end":
                            currentQuestion.vertical_alignment === "bottom",
                        },
                      ])}
                    >
                      {/* Show image between description and choices if position is bottom */}
                      <div
                        className={clsx([
                          "flex flex-col gap-4",
                          currentQuestion.mobile_layout ===
                          "landscape_image_middle"
                            ? "flex-col"
                            : "flex-col-reverse",
                        ])}
                      >
                        {/* Content */}
                        <div
                          className={clsx(
                            currentQuestion.center_text && "text-center"
                          )}
                        >
                          <QuestionContent question={currentQuestion} />
                        </div>

                        {/* Background image / video */}
                        <BackgroundMedia
                          question={currentQuestion}
                          displayType="mobile"
                          layout={currentQuestion.mobile_layout}
                        />
                      </div>

                      {/* Choices grid */}
                      <ChoicesGrid
                        question={currentQuestion}
                        onChoiceSelect={handleChoiceSelection}
                        handleOtherSubmit={handleOtherSubmit}
                        onPrevious={handlePrevious}
                        onNext={handleNext}
                        canGoBack={canGoBack}
                        canGoForward={canGoForward}
                        isMobile={true}
                        resetForm={resetForm}
                      />
                    </div>
                  )}

                  {/* For full_image_top, the image is on the top of the screen, full width */}
                  {(!currentQuestion.mobile_layout ||
                    currentQuestion.mobile_layout ===
                      Enum_Componentcmsdataformquestion_Mobile_Layout.FullImageTop) && (
                    <div className="flex flex-col min-h-screen">
                      {/* Image / video container - always at top */}
                      <BackgroundMedia
                        question={currentQuestion}
                        displayType="mobile"
                        layout={
                          currentQuestion.mobile_layout ||
                          Enum_Componentcmsdataformquestion_Mobile_Layout.FullImageTop
                        }
                      />

                      {/* Content container - fills remaining space with vertical alignment */}
                      <div
                        className={clsx([
                          "flex-1 flex flex-col p-2 max-w-sm mx-auto",
                          {
                            "justify-start":
                              currentQuestion.vertical_alignment === "top",
                            "justify-center":
                              currentQuestion.vertical_alignment === "middle",
                            "justify-end":
                              currentQuestion.vertical_alignment === "bottom",
                          },
                        ])}
                      >
                        <div className={clsx(["flex flex-col gap-4"])}>
                          <div
                            className={clsx(
                              currentQuestion.center_text && "text-center"
                            )}
                          >
                            <QuestionContent question={currentQuestion} />
                          </div>

                          {/* Choices grid */}
                          <ChoicesGrid
                            question={currentQuestion}
                            onChoiceSelect={handleChoiceSelection}
                            handleOtherSubmit={handleOtherSubmit}
                            onPrevious={handlePrevious}
                            onNext={handleNext}
                            canGoBack={canGoBack}
                            canGoForward={canGoForward}
                            isMobile={true}
                            resetForm={resetForm}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                {/* Desktop layout */}
                <div className="relative lg:block hidden">
                  {/* For full_background desktop layout, image is in the background */}
                  {currentQuestion?.desktop_layout ===
                    Enum_Componentcmsdataformquestion_Desktop_Layout.FullBackground && (
                    <>
                      {/* Background image / video */}
                      <BackgroundMedia
                        question={currentQuestion}
                        displayType="desktop"
                        layout={currentQuestion.desktop_layout}
                      />

                      {/* Content with higher z-index */}
                      <div
                        className={clsx([
                          "relative z-10 min-h-screen flex flex-col max-w-screen-md mx-auto",
                          {
                            "justify-start":
                              currentQuestion.vertical_alignment === "top",
                            "justify-center":
                              currentQuestion.vertical_alignment === "middle",
                            "justify-end":
                              currentQuestion.vertical_alignment === "bottom",
                          },
                        ])}
                      >
                        <div className="p-4">
                          <QuestionContent question={currentQuestion} />
                          {/* Choices grid */}
                          <ChoicesGrid
                            question={currentQuestion}
                            onChoiceSelect={handleChoiceSelection}
                            handleOtherSubmit={handleOtherSubmit}
                            onPrevious={handlePrevious}
                            onNext={handleNext}
                            canGoBack={canGoBack}
                            canGoForward={canGoForward}
                            isMobile={false}
                            resetForm={resetForm}
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {/* For landscape_image_left or landscape_image_right layout, the image is on the left or right side of the screen */}
                  {(currentQuestion.desktop_layout ===
                    Enum_Componentcmsdataformquestion_Desktop_Layout.LandscapeImageLeft ||
                    currentQuestion.desktop_layout ===
                      Enum_Componentcmsdataformquestion_Desktop_Layout.LandscapeImageRight ||
                    currentQuestion.desktop_layout ===
                      Enum_Componentcmsdataformquestion_Desktop_Layout.StartScreen) && (
                    // Side-by-side layout
                    <div
                      className={clsx(
                        "flex gap-8 px-5 py-10",
                        currentQuestion.desktop_layout ===
                          Enum_Componentcmsdataformquestion_Desktop_Layout.LandscapeImageRight
                          ? "flex-row"
                          : "flex-row-reverse",
                        currentQuestion.desktop_layout !==
                          Enum_Componentcmsdataformquestion_Desktop_Layout.StartScreen
                          ? "min-h-screen"
                          : "max-w-screen-md mx-auto",
                        // Add vertical alignment classes
                        {
                          "items-start":
                            currentQuestion.vertical_alignment === "top",
                          "items-center":
                            currentQuestion.vertical_alignment === "middle",
                          "items-end":
                            currentQuestion.vertical_alignment === "bottom",
                        }
                      )}
                    >
                      {/* Content side */}
                      <div className="flex-1 px-10 flex flex-col gap-4">
                        <QuestionContent question={currentQuestion} />

                        {/* Choices grid */}
                        <ChoicesGrid
                          question={currentQuestion}
                          onChoiceSelect={handleChoiceSelection}
                          handleOtherSubmit={handleOtherSubmit}
                          onPrevious={handlePrevious}
                          onNext={handleNext}
                          canGoBack={canGoBack}
                          canGoForward={canGoForward}
                          isMobile={false}
                          resetForm={resetForm}
                        />
                      </div>

                      {/* Image / video side */}
                      <BackgroundMedia
                        question={currentQuestion}
                        displayType="desktop"
                        layout={currentQuestion.desktop_layout}
                      />
                    </div>
                  )}

                  {/* For landscape_image_middle layout, the image is in the middle of the screen */}
                  {(!currentQuestion.desktop_layout ||
                    currentQuestion.desktop_layout ===
                      Enum_Componentcmsdataformquestion_Desktop_Layout.LandscapeImageMiddle) && (
                    <div
                      className={clsx([
                        "flex flex-col gap-4 min-h-screen p-4 max-w-screen-md mx-auto",
                        currentQuestion.center_text && "text-center",
                        // Add vertical alignment classes
                        {
                          "justify-start":
                            currentQuestion.vertical_alignment === "top",
                          "justify-center":
                            currentQuestion.vertical_alignment === "middle",
                          "justify-end":
                            currentQuestion.vertical_alignment === "bottom",
                        },
                      ])}
                    >
                      {/* Content */}
                      <div
                        className={clsx(
                          currentQuestion.center_text && "text-center"
                        )}
                      >
                        <QuestionContent question={currentQuestion} />
                      </div>

                      {/* Show image between description and choices if position is bottom */}
                      <BackgroundMedia
                        question={currentQuestion}
                        displayType="desktop"
                        layout={
                          currentQuestion.desktop_layout ||
                          Enum_Componentcmsdataformquestion_Desktop_Layout.LandscapeImageMiddle
                        }
                      />

                      {/* Choices grid */}
                      <ChoicesGrid
                        question={currentQuestion}
                        onChoiceSelect={handleChoiceSelection}
                        handleOtherSubmit={handleOtherSubmit}
                        onPrevious={handlePrevious}
                        onNext={handleNext}
                        canGoBack={canGoBack}
                        canGoForward={canGoForward}
                        isMobile={false}
                        resetForm={resetForm}
                      />
                    </div>
                  )}

                  {/* For full_image_right or full_image_left, the image is on the right or left side of the screen */}
                  {(currentQuestion.desktop_layout ===
                    Enum_Componentcmsdataformquestion_Desktop_Layout.FullImageRight ||
                    currentQuestion.desktop_layout ===
                      Enum_Componentcmsdataformquestion_Desktop_Layout.FullImageLeft) && (
                    <div
                      className={clsx([
                        "flex h-[100vh]",
                        currentQuestion.desktop_layout ===
                        Enum_Componentcmsdataformquestion_Desktop_Layout.FullImageRight
                          ? "flex-row"
                          : "flex-row-reverse",
                        // Add vertical alignment classes
                        {
                          "items-start":
                            currentQuestion.vertical_alignment === "top",
                          "items-center":
                            currentQuestion.vertical_alignment === "middle",
                          "items-end":
                            currentQuestion.vertical_alignment === "bottom",
                        },
                      ])}
                    >
                      {/* Content side */}
                      <div className="flex-1 flex flex-col py-4 px-4 overflow-y-auto">
                        <div
                          className={clsx(
                            currentQuestion.center_text && "text-center"
                          )}
                        >
                          <QuestionContent question={currentQuestion} />
                        </div>

                        {/* Choices grid */}
                        <ChoicesGrid
                          question={currentQuestion}
                          onChoiceSelect={handleChoiceSelection}
                          handleOtherSubmit={handleOtherSubmit}
                          onPrevious={handlePrevious}
                          onNext={handleNext}
                          canGoBack={canGoBack}
                          canGoForward={canGoForward}
                          isMobile={false}
                          resetForm={resetForm}
                        />
                      </div>

                      {/* Image / video side */}
                      <BackgroundMedia
                        question={currentQuestion}
                        displayType="desktop"
                        layout={currentQuestion.desktop_layout}
                      />
                    </div>
                  )}
                </div>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// New component to preload the next question's media
const PreloadNextQuestion: React.FC<{
  question: ComponentCmsDataFormQuestion;
  isMobile: boolean;
}> = ({ question, isMobile }) => {
  // Determine which layout to use
  const layout = isMobile ? question.mobile_layout : question.desktop_layout;

  // Preload media based on question type
  return (
    <>
      {/* Preload question media (image or video) */}
      {question.video?.data?.attributes?.url && (
        <video
          src={question.video?.data?.attributes?.url}
          preload="auto"
          style={{ display: "none" }}
        />
      )}
      {question.image?.data?.attributes?.url && (
        <img
          src={question.image?.data?.attributes?.url}
          style={{ display: "none" }}
          alt="Preloaded background"
        />
      )}

      {question.feedback_media?.data?.attributes?.url && (
        <>
          {question.feedback_media?.data?.attributes?.mime?.startsWith(
            "video/"
          ) && (
            <video
              src={question.feedback_media?.data?.attributes?.url}
              preload="auto"
              style={{ display: "none" }}
            />
          )}
          {question.feedback_media?.data?.attributes?.mime?.startsWith(
            "image/"
          ) && (
            <img
              src={question.feedback_media?.data?.attributes?.url}
              style={{ display: "none" }}
              alt="Preloaded feedback media"
            />
          )}
        </>
      )}

      {/* Preload choice media */}
      {question.choices?.map((choice) => {
        return (
          <>
            {choice?.choice_media?.data?.attributes?.url && (
              <>
                {choice.choice_media?.data?.attributes?.mime?.startsWith(
                  "video/"
                ) && (
                  <video
                    key={choice.id}
                    src={choice.choice_media.data.attributes.url}
                    preload="auto"
                    style={{ display: "none" }}
                  />
                )}
                {choice.choice_media?.data?.attributes?.mime?.startsWith(
                  "image/"
                ) && (
                  <img
                    key={choice.id}
                    src={choice.choice_media.data.attributes.url}
                    style={{ display: "none" }}
                    alt={`Preloaded choice media ${choice.id}`}
                  />
                )}
              </>
            )}
            {choice?.feedback_media?.data?.attributes?.url && (
              <>
                {choice.feedback_media?.data?.attributes?.mime?.startsWith(
                  "video/"
                ) && (
                  <video
                    key={choice.id}
                    src={choice.feedback_media.data.attributes.url}
                    preload="auto"
                    style={{ display: "none" }}
                  />
                )}
                {choice.feedback_media?.data?.attributes?.mime?.startsWith(
                  "image/"
                ) && (
                  <img
                    key={choice.id}
                    src={choice.feedback_media.data.attributes.url}
                    style={{ display: "none" }}
                    alt={`Preloaded choice feedback media ${choice.id}`}
                  />
                )}
              </>
            )}
          </>
        );
      })}
    </>
  );
};

export default LFormGroup;
